import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Plus, Check, Flame, Pencil, Trash2, X, Loader2, Settings2 } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import Modal from '../../components/ui/Modal'
import { useHabits } from '../../hooks/useHabits'
import { cn } from '../../lib/utils'

const EMOJI_CHOICES = ['💪', '📖', '🧘', '💧', '🏃', '🛏️', '🚭', '🥗', '🙏', '✍️', '🎯', '☀️']
const COLOR_CHOICES = ['emerald', 'sky', 'violet', 'amber', 'rose', 'teal']
const COLOR_CLASS = {
  emerald: 'bg-emerald-500', sky: 'bg-sky-500', violet: 'bg-violet-500',
  amber: 'bg-amber-500', rose: 'bg-rose-500', teal: 'bg-teal-500',
}

export function HabitsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { habits, loading, isDone, toggle, addHabit, updateHabit, archiveHabit, streakFor } = useHabits()

  const [editing, setEditing] = useState(false)
  const [editor, setEditor] = useState(null) // null | {} (new) | habit (edit)

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">
          {t('habits.title')}
        </h1>
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          aria-label={t('habits.manage')}
          className={cn('p-2 rounded-md hover:bg-accent', editing ? 'text-app-primary-500' : 'text-muted-foreground')}
        >
          <Settings2 className="w-5 h-5" />
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
        </div>
      )}

      {!loading && habits.length === 0 && (
        <Card>
          <CardContent className="p-6 text-center text-sm text-muted-foreground" dir="auto">
            {t('habits.empty')}
          </CardContent>
        </Card>
      )}

      {!loading && habits.length > 0 && (
        <Card>
          <CardContent className="p-0">
            {habits.map((h, i) => {
              const done = isDone(h.id)
              const streak = streakFor(h.id)
              return (
                <div
                  key={h.id}
                  className={cn(
                    'w-full p-3 flex items-center gap-3',
                    i < habits.length - 1 && 'border-b border-border/60',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => !editing && toggle(h.id)}
                    disabled={editing}
                    className={cn(
                      'w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-colors',
                      done ? `${COLOR_CLASS[h.color] || 'bg-app-primary-500'} text-white` : 'bg-muted text-muted-foreground',
                    )}
                    aria-pressed={done}
                  >
                    {h.emoji ? <span className="text-base">{h.emoji}</span> : <Check className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-sm truncate', done ? 'text-foreground font-medium' : 'text-foreground')} dir="auto">
                      {h.name}
                    </p>
                    {streak > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[11px] text-amber-500" dir="auto">
                        <Flame className="w-3 h-3" /> {t('habits.streakN', { n: streak })}
                      </span>
                    )}
                  </div>
                  {editing ? (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button type="button" onClick={() => setEditor(h)} aria-label={t('common.edit')} className="p-2 rounded-md hover:bg-accent text-muted-foreground">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => archiveHabit(h.id)} aria-label={t('common.delete')} className="p-2 rounded-md hover:bg-accent text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    done && <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  )}
                </div>
              )
            })}
          </CardContent>
        </Card>
      )}

      {!loading && (
        <button
          type="button"
          onClick={() => setEditor({})}
          className="w-full py-3 rounded-xl border-2 border-dashed border-border text-sm text-muted-foreground inline-flex items-center justify-center gap-1.5"
          dir="auto"
        >
          <Plus className="w-4 h-4" /> {t('habits.add')}
        </button>
      )}

      {editor && (
        <HabitEditor
          habit={editor.id ? editor : null}
          onClose={() => setEditor(null)}
          onSave={async (patch) => {
            if (editor.id) await updateHabit(editor.id, patch)
            else await addHabit(patch)
            setEditor(null)
          }}
          t={t}
        />
      )}
    </div>
  )
}

function HabitEditor({ habit, onClose, onSave, t }) {
  const [name, setName] = useState(habit?.name || '')
  const [emoji, setEmoji] = useState(habit?.emoji || EMOJI_CHOICES[0])
  const [color, setColor] = useState(habit?.color || COLOR_CHOICES[0])
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!name.trim() || busy) return
    setBusy(true)
    await onSave({ name: name.trim(), emoji, color })
    setBusy(false)
  }

  return (
    <Modal open onClose={onClose} ariaLabel={habit ? t('habits.editTitle') : t('habits.add')}>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="flex-1 text-base font-bold text-foreground" dir="auto">
          {habit ? t('habits.editTitle') : t('habits.add')}
        </h2>
        <button type="button" onClick={onClose} aria-label={t('settings.close')} className="p-1.5 rounded-md hover:bg-accent text-muted-foreground">
          <X className="w-5 h-5" />
        </button>
      </div>

      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('habits.namePlaceholder')}
        className="w-full h-10 px-3 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none mb-3"
      />

      <p className="text-[11px] text-muted-foreground mb-1" dir="auto">{t('habits.icon')}</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {EMOJI_CHOICES.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => setEmoji(e)}
            className={cn('w-9 h-9 rounded-lg text-lg flex items-center justify-center', emoji === e ? 'bg-app-primary-500/20 ring-2 ring-app-primary-500' : 'bg-muted')}
          >
            {e}
          </button>
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground mb-1" dir="auto">{t('habits.color')}</p>
      <div className="flex gap-2 mb-4">
        {COLOR_CHOICES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setColor(c)}
            aria-label={c}
            className={cn('w-8 h-8 rounded-full', COLOR_CLASS[c], color === c && 'ring-2 ring-offset-2 ring-offset-card ring-foreground/50')}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={save}
        disabled={!name.trim() || busy}
        className="w-full py-2.5 rounded-lg bg-app-primary-500 text-white text-sm font-semibold disabled:opacity-50"
        dir="auto"
      >
        {busy ? t('common.saving') : t('common.save')}
      </button>
    </Modal>
  )
}

export default HabitsPage
